/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button, Divider, Grid, Hidden } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import {
  InboundTaskItemAssignType,
  InboundTaskItemStatusEnum,
} from 'commons/enums';
import { IInboundTaskDocument, IInboundTaskItem } from 'commons/types';
import {
  actionAssignUnassignInboundTaskItem,
  actionBulkUnassignTaskItemByInboundTaskNumber,
  createInboundInvoiceAction,
  getInProgressInboundTaskDocument,
  getUserInProgressInboundTaskDocumentAndInvoice,
} from 'redux-stores/actions';
import { selectUser } from 'redux-stores/features/authSlice';
import { RootReducerInterface } from 'redux-stores/reducers';
import { setAssignedInboundTaskItem } from 'redux-stores/reducers/inboundTaskItemReducer';
import { AppDispatch } from 'redux-stores/store';
import { INBOUND_ROUTES } from 'routes/constants';
import { InboundInvoicePrepopulatedData } from 'services/apis/SwipeRxWmsApiInboundInvoice/type';

import { CreateInboundInvoiceDialog } from './components/CreateInboundInvoiceDialog.component';
import { InboundItemAssignStyle as S } from './InboundItemAssign.styles';

export const InboundItemAssignPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { searchedQueueDocument } = useSelector(
    (state: RootReducerInterface) => state.inboundTaskDocument,
  );
  const { inboundInvoice } = useSelector(
    (state: RootReducerInterface) => state.inboundInvoice,
  );
  const { assignedInboundTaskItems } = useSelector(
    (state: RootReducerInterface) => state.inboundTaskItem,
  );
  const user = useSelector(selectUser);

  const [filter, setFilter] = useState<string>('');
  const [searchedFilter, setSearchedFilter] = useState<string>('');
  const [showModalChangeDocument, setShowModalChangeDocument] =
    useState<boolean>(false);
  const [showModalInputInvoice, setShowModalInputInvoice] =
    useState<boolean>(false);
  const [showModalAssignTaskItem, setShowModalAssignTaskItem] =
    useState<IInboundTaskItem | null>(null);
  const [inboundInvoiceData, setInboundInvoiceData] =
    useState<InboundInvoicePrepopulatedData>({
      invoice_number: '',
      invoice_date: new Date().toISOString(),
    });

  const progressToReceivingProcess = async (
    isNewInvoice?: boolean,
  ): Promise<void> => {
    if (searchedQueueDocument) {
      if (isNewInvoice) {
        await dispatch(
          createInboundInvoiceAction({
            payload: {
              ...inboundInvoiceData,
              invoice_date: new Date(inboundInvoiceData.invoice_date),
              inbound_task_number: searchedQueueDocument.inbound_task_number,
              warehouse_id: searchedQueueDocument.warehouse_id,
              sku_codes:
                searchedQueueDocument.items
                  ?.filter(
                    (item) =>
                      item.receiver === user.userName &&
                      item.status === InboundTaskItemStatusEnum.ASSIGNED,
                  )
                  .map((item) => {
                    return { sku_code: item.sku_code, foc: item.foc };
                  }) || [],
            },
            navigate,
          }),
        );
      } else {
        navigate(INBOUND_ROUTES.LIST);
      }
    }
  };

  const handleStartInboundTask = async (): Promise<void> => {
    if (!inboundInvoice) {
      setShowModalInputInvoice(true);
    } else {
      progressToReceivingProcess();
    }
  };

  const handleRefresh = async (): Promise<void> => {
    dispatch(getUserInProgressInboundTaskDocumentAndInvoice());
  };

  useEffect(() => {
    dispatch(getUserInProgressInboundTaskDocumentAndInvoice());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      setAssignedInboundTaskItem(
        searchedQueueDocument?.items?.filter(
          (item) =>
            item.receiver === user.userName && [
              InboundTaskItemStatusEnum.ASSIGNED,
              InboundTaskItemStatusEnum.RECEIVING,
            ],
        ) || [],
      ),
    );

    setInboundInvoiceData({
      invoice_date: inboundInvoice?.invoice_date || new Date().toISOString(),
      invoice_number: inboundInvoice?.invoice_number || '',
    });
  }, [inboundInvoice, searchedQueueDocument, dispatch]);

  const proceedGetDocument = (): void => {
    if (!inboundInvoice && assignedInboundTaskItems.length > 0)
      dispatch(
        actionBulkUnassignTaskItemByInboundTaskNumber({
          inbound_task_number: assignedInboundTaskItems[0].inbound_task_number,
        }),
      );
    dispatch(getInProgressInboundTaskDocument({ inboundTaskNumber: filter }));
    setSearchedFilter(filter);
  };

  const handleClickGetPO = (): void => {
    if (!searchedQueueDocument) {
      proceedGetDocument();
    } else if (searchedQueueDocument && filter !== searchedFilter) {
      setShowModalChangeDocument(true);
    }
  };

  const proceedAssignUnassignTaskItem = (
    taskItem: IInboundTaskItem,
    assignType: InboundTaskItemAssignType,
  ): void => {
    dispatch(
      actionAssignUnassignInboundTaskItem({
        item: taskItem,
        assignType,
        username: user.userName,
      }),
    );
    setSearchedFilter(filter);
  };

  const handleAssignTaskItem = (taskItem: IInboundTaskItem): void => {
    if (!taskItem.receiver) {
      proceedAssignUnassignTaskItem(taskItem, InboundTaskItemAssignType.ASSIGN);
    } else {
      setShowModalAssignTaskItem(taskItem);
    }
  };

  // render additional section
  const renderTaskItemList = (
    document: IInboundTaskDocument,
  ): React.JSX.Element => (
    <div>
      {/* main info */}

      <S.TaskDocumentMainInfoWrapper container>
        <S.DocumentNumberText item xs={12} md={4.5}>
          {document.inbound_task_number}
          <Button sx={{ marginBottom: '2px' }} onClick={handleRefresh}>
            REFRESH
          </Button>
        </S.DocumentNumberText>
        <S.VendorText
          item
          xs={12}
          md={7.5}
          sx={{ paddingTop: { xs: 0, md: '8px' } }}
        >
          {document.vendor}
        </S.VendorText>
      </S.TaskDocumentMainInfoWrapper>

      {/* item info */}
      {document.items &&
        document.items.map((item, idx) => (
          <>
            {/* eslint-disable-next-line react/no-array-index-key */}
            <S.TaskItemWrapper container key={`inbound-assign-header-${idx}`}>
              <S.TaskItemSkuCode item xs={4} sm={2}>
                {item.sku_code}
                <br /> {item.foc && <S.FOCText>FOC</S.FOCText>}
              </S.TaskItemSkuCode>
              <Hidden smDown>
                <S.TaskItemDescription item xs={4}>
                  {item.name} {item.uom}
                </S.TaskItemDescription>
                <S.TaskItemQTY item xs={2}>
                  <div style={{ textAlign: 'center', paddingRight: 8 }}>
                    QTY: {item.quantity}
                  </div>
                </S.TaskItemQTY>
              </Hidden>

              <Grid item xs={4} sm={2}>
                <S.TaskItemStatus
                  status={item.status}
                  isSameUser={item.receiver === user.userName}
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                <S.TaskItemAssignButton
                  status={item.status}
                  isSameUser={item.receiver === user.userName}
                  onClick={() => handleAssignTaskItem(item)}
                />
              </Grid>
              <Hidden smUp>
                <S.TaskItemDescription item xs={12}>
                  {item.name} {item.uom}
                </S.TaskItemDescription>
                <S.TaskItemQTY item xs={12}>
                  <div>QTY: {item.quantity}</div>
                </S.TaskItemQTY>
              </Hidden>
            </S.TaskItemWrapper>
            <Divider />
          </>
        ))}
    </div>
  );

  return (
    <div>
      <S.ContentWrapper>
        {/* Search bar section */}
        <Grid container style={{ padding: '8px 0' }} spacing={1}>
          <Grid item xs={8} sm={9}>
            <S.SearchBar
              placeholder="Input PO Number"
              variant="outlined"
              disabled={
                !!inboundInvoice &&
                inboundInvoice.items &&
                inboundInvoice.items.length > 0
              }
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              inputProps={{
                'data-testid': 'InboundItemSearchPO',
              }}
            />
          </Grid>
          <Grid item xs={4} sm={3}>
            <S.PrimaryButton
              onClick={() => handleClickGetPO()}
              disabled={!filter || !!inboundInvoice}
              data-testid="BtnCheckDetails"
              sx={{ width: '100% !important' }}
            >
              GET PO
            </S.PrimaryButton>
          </Grid>
        </Grid>

        {/* Inbound Task Items Section */}
        {searchedQueueDocument && renderTaskItemList(searchedQueueDocument)}
      </S.ContentWrapper>

      {/* Footer */}
      <S.StickyFooterWrapper>
        <S.PrimaryButton
          disabled={assignedInboundTaskItems.length === 0}
          onClick={handleStartInboundTask}
        >
          START TASK
        </S.PrimaryButton>
      </S.StickyFooterWrapper>
      <ConfirmationDialog
        title="Change PO"
        content="Are you sure want to redo the task assignment and change PO?"
        onClose={() => setShowModalChangeDocument(false)}
        openModal={showModalChangeDocument}
        onConfirm={() => proceedGetDocument()}
      />
      <ConfirmationDialog
        title="Task Unassignment"
        content="Are you sure want to unassign this task?"
        onClose={() => setShowModalAssignTaskItem(null)}
        openModal={!!showModalAssignTaskItem}
        onConfirm={() =>
          proceedAssignUnassignTaskItem(
            showModalAssignTaskItem as IInboundTaskItem,
            InboundTaskItemAssignType.UNASSIGN,
          )
        }
      />
      <CreateInboundInvoiceDialog
        onClose={() => setShowModalInputInvoice(false)}
        inboundInvoiceData={inboundInvoiceData}
        setInboundInvoiceData={setInboundInvoiceData}
        openModal={showModalInputInvoice}
        progressToReceivingProcess={progressToReceivingProcess}
      />
    </div>
  );
};
